import ObjectModelProperty from "../../objectModelProperties/objectModelProperty";
import ValidationError from "../../validationError";
import _ from "lodash";
import {BaseObjectModelEntryValidator} from "./baseObjectModelEntryValidator";
import {ObjectModelPropertyCapability} from "../../objectModelProperties/objectModelPropertyCapability";
import ObjectModelEntry from "../objectModelEntry";
import {OptionRangeType} from "../../../enums/optionRangeType";
import pluralizeable from "../../../mixins/pluralizeable";
import {InputFormatType} from "../../../../formability/form_builder/models/inputFormatType";
import InputFormatDefinition from "../../inputFormatDefinition";
import {RegexHelper} from "../../../helpers/regexHelper";
import {ObjectModelPropertyRootType} from "../../../enums/objectModelPropertyType";
import {PickListSelectionType} from "~/enums/pickListSelectionType";

export abstract class ObjectModelEntryPropertyValidator extends BaseObjectModelEntryValidator {
  property: ObjectModelProperty;

  protected constructor(entry: ObjectModelEntry, property: ObjectModelProperty) {
    super(entry);
    this.property = property;
  }

  protected get entryValue(): string | string[] | number {
    return this.entry[`f_${this.property.client_id}`];
  }

  protected buildValidationError(message: string): ValidationError {
    return new ValidationError({message, field: this.property.client_id});
  }

  static validatorsFor(entry: ObjectModelEntry, property: ObjectModelProperty): ObjectModelEntryPropertyValidator[] {
    const validators = [];
    if (property.can(ObjectModelPropertyCapability.requireable)) {
      validators.push(new RequiredValidator(entry, property));
    }
    if (property.can(ObjectModelPropertyCapability.lengthable)) {
      validators.push(new LengthValidator(entry, property));
    }
    if (property.can(ObjectModelPropertyCapability.numericMinMaxable)) {
      validators.push(new NumericRangeValidator(entry, property));
      validators.push(new NumericDecimalPlaceValidator(entry, property));
    }
    if (property.can(ObjectModelPropertyCapability.optionRangeRequireable)) {
      validators.push(new OptionRangeValidator(entry, property));
    }
    if (property.can(ObjectModelPropertyCapability.formattable)) {
      validators.push(new FormatValidator(entry, property));
    }
    return validators;
  }
}

class RequiredValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    if (!this.property.is_required) return;
    if (this.entryValue === null || this.entryValue === undefined || _.isNaN(this.entryValue) ||
      (typeof this.entryValue !== 'number' && this.entryValue.length === 0) ||
      (this.property.property_type === ObjectModelPropertyRootType.FileAttachment && this.entryValue === 0))
    {
      let errorMessage: string;
      switch (this.property.property_type) {
        case ObjectModelPropertyRootType.PickList:
          errorMessage = 'Please select an option';
          break;
        case ObjectModelPropertyRootType.FileAttachment:
          // if a file upload has been saved previously, then we pass validations
          if (this.entry.file_uploads && this.entry.file_uploads.find(upload => upload.property_client_id === this.property.client_id)) {
            return;
          }
          errorMessage = 'Please upload a file.';
          break;
        default:
          errorMessage = 'Please enter a value';
          break;
      }
      return this.buildValidationError(errorMessage);
    }
  }
}

class LengthValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    const min = this.property.character_count.min;
    const max = this.property.character_count.max ;
    if (!this.entryValue || typeof this.entryValue !== 'string') return;
    if ((min && this.entryValue.length < min) || (max && this.entryValue.length > max)) {
      let message = this.property.character_count.char_limit_message;
      if (!message) {
        if (min && max) {
          message = `Please enter between ${min} and ${max} characters`;
        } else if (min) {
          message = `Please enter at least ${pluralizeable.filters.pluralize('character', min)}`;
        } else if (max) {
          message = `Please enter at most ${pluralizeable.filters.pluralize('character', max)}`;
        }
      }
      return this.buildValidationError(message);
    }
  }
}

class NumericRangeValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    if (typeof this.entryValue === 'object') return;
    const value = typeof this.entryValue === 'number' ? this.entryValue : parseFloat(this.entryValue);
    const min = this.property.min_value;
    const max = this.property.max_value;
    if ((min !== null && value < min) || (max !== null && value > max)) {
      const message = this.property.format_message || `Please enter a value between ${min} and ${max}`;
      return this.buildValidationError(message);
    }
  }
}

class NumericDecimalPlaceValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    if (typeof this.entryValue === 'object') return;
    const value = typeof this.entryValue === 'number' ? this.entryValue : parseFloat(this.entryValue);
    if (Math.floor(value) !== value) {
      const currentPlaces = value.toString().split('.')[1];
      if (currentPlaces && currentPlaces.length > this.property.decimal_places) {
        const message = `Value cannot have more than ${this.property.decimal_places} decimal places`;
        return this.buildValidationError(message);
      }
    }
  }
}

class OptionRangeValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    if (typeof this.entryValue !== 'object') return;
    if (this.property.selection_type === PickListSelectionType.SingleSelect) return;
    if (!this.property.is_required) return;

    const selectedOptionsCount = _.uniq(this.entryValue).length;
    switch (this.property.option_range_type) {
      case OptionRangeType.AtLeast:
        if (selectedOptionsCount < this.property.checkbox_range_min) {
          const options = pluralizeable.filters.pluralize('option', this.property.checkbox_range_min);
          return this.buildValidationError(`Please select at least ${options}`);
        }
        break;
      case OptionRangeType.AtMost:
        if (selectedOptionsCount > this.property.checkbox_range_max) {
          const options = pluralizeable.filters.pluralize('option', this.property.checkbox_range_max);
          return this.buildValidationError(`Please select at most ${options}`);
        }
        break;
      case OptionRangeType.Exactly:
        if (selectedOptionsCount !== this.property.checkbox_range_min) {
          const options = pluralizeable.filters.pluralize('option', this.property.checkbox_range_min);
          return this.buildValidationError(`Please select exactly ${options}`);
        }
        break;
      case OptionRangeType.Between:
        if (selectedOptionsCount < this.property.checkbox_range_min || selectedOptionsCount > this.property.checkbox_range_max) {
          return this.buildValidationError(`Please select between ${this.property.checkbox_range_min} and ${this.property.checkbox_range_max} options`);
        }
        break;
      case OptionRangeType.All:
        if (selectedOptionsCount !== this.property.objectModelPropertiesArray.length) {
          return this.buildValidationError('Please select all options');
        }
        break;
    }
  }
}

class FormatValidator extends ObjectModelEntryPropertyValidator {
  protected _validate(): ValidationError | null {
    if (!this.entryValue || typeof this.entryValue !== 'string' || !this.property.format_type || this.property.format_type === InputFormatType.None) return;
    if (!this.matcher.test(this.entryValue)) {
      return this.buildValidationError(`Must match ${InputFormatDefinition.getDisplayName(this.property.format_type)} format`);
    }
  }

  get matcher(): RegExp {
    switch (this.property.format_type) {
      case InputFormatType.Numeric:
      case InputFormatType.Currency:
        return RegexHelper.number;
      case InputFormatType.Alphanumeric:
        return RegexHelper.alphanumeric;
      case InputFormatType.Email:
        return RegexHelper.emailOrBlank;
      case InputFormatType.Phone:
        return RegexHelper.phone;
      case InputFormatType.Zip:
        return RegexHelper.zip;
      case InputFormatType.Url:
        return RegexHelper.url;
      case InputFormatType.SocialSecurity:
        return RegexHelper.socialSecurity;
      case InputFormatType.Pattern:
        return new RegExp(this.property.format_pattern);
    }
  }
}